import { Observable, catchError, map, of, shareReplay, startWith, switchMap, timer } from 'rxjs';
import { Character } from '../models/character';
import { CharacterFilters } from '../types/CharacterFilters';

export type CharacterSummary = Pick<Character,
	'_id' | 'name' | 'imageUrl' | 'isPNJ' | 'isPublic' | 'level' | 'age' | 'race' | 'lineage'> & {
	owner: { _id: string; name: string } | null;
};

export interface CharacterSearchRequest extends CharacterFilters {
	search: string;
	mine: boolean;
	isPNJ?: boolean;
	page: number;
	pageSize: number;
}

export interface CharacterSearchResult {
	items: CharacterSummary[];
	total: number;
	page: number;
	pageSize: number;
}

export interface CharacterSearchState extends CharacterSearchResult {
	loading: boolean;
	error: string | null;
}

export function createDefaultCharacterFilters(): CharacterFilters {
	return {
		publishedOnly: true, upToDate: false, mjApproved: false, beginners: false, hasImage: false,
		levelRange: [1, 10], ageRange: [0, 1000], races: [], selectedCampaign: '',
		strengthRange: [30, 85], agilityRange: [30, 85], enduranceRange: [30, 85],
		socialRange: [30, 85], mentalRange: [30, 85],
	};
}

export function clampAgeRange(range: [number, number], changed: 'min' | 'max'): [number, number] {
	const step = 10;
	let min = Math.max(0, Math.min(990, Math.round(range[0] / step) * step));
	let max = Math.max(10, Math.min(1000, Math.round(range[1] / step) * step));
	if (changed === 'min') min = Math.min(min, max - step);
	else max = Math.max(max, min + step);
	return [min, max];
}

export function createCharacterSearchState(
	requests: Observable<CharacterSearchRequest>,
	search: (request: CharacterSearchRequest) => Observable<CharacterSearchResult>,
	delayMs = 200,
): Observable<CharacterSearchState> {
	return requests.pipe(
		// Cancel the old request immediately, then debounce the next one.
		switchMap((request) => {
			const empty = { items: [], total: 0, page: request.page, pageSize: request.pageSize };
			return timer(delayMs).pipe(
				switchMap(() => search(request)),
				map((result): CharacterSearchState => {
					if (!result || !Array.isArray(result.items) || !Number.isFinite(result.total)) {
						throw new Error('Invalid character search response');
					}
					return { ...result, loading: false, error: null };
				}),
				catchError(() => of<CharacterSearchState>({
					...empty, loading: false,
					error: 'Impossible de charger les personnages. Réessayez dans quelques instants.',
				})),
				startWith<CharacterSearchState>({ ...empty, loading: true, error: null }),
			);
		}),
		shareReplay({ bufferSize: 1, refCount: true }),
	);
}
